import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, CreditCard, Wrench, Briefcase, CalendarDays, CheckCircle2, Clock, Settings, ExternalLink, BadgeCheck } from "lucide-react";
import { Tier } from "@/components/Shared";
import ImageUpload from "@/components/ImageUpload";
import { useConfig } from "@/lib/useConfig";

const field = "w-full bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#00F0FF] focus:outline-none";

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" data-testid="dashboard-modal">
    <div className="card-surface rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display font-bold text-xl">{title}</h3>
        <button onClick={onClose} data-testid="modal-close"><X className="w-5 h-5 text-[#94A3B8] hover:text-white" /></button>
      </div>
      {children}
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  if (status === "active") return <span className="flex items-center gap-1 text-xs text-[#10B981]"><CheckCircle2 className="w-3.5 h-3.5" /> Live</span>;
  return <span className="flex items-center gap-1 text-xs text-[#F59E0B]"><Clock className="w-3.5 h-3.5" /> Awaiting payment</span>;
};

export default function Dashboard() {
  const { user } = useAuth();
  const { paymentsEnabled } = useConfig();
  const [data, setData] = useState({ tools: [], jobs: [], events: [] });
  const [tab, setTab] = useState("tools");
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toolLogo, setToolLogo] = useState("");
  const [eventBanner, setEventBanner] = useState("");
  const [profileLogo, setProfileLogo] = useState("");

  const load = () => api.get("/my/listings").then((r) => setData(r.data));
  const [subInfo, setSubInfo] = useState(null);
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/my/subscription").then((r) => setSubInfo(r.data)).catch(() => {}); }, []);

  const startCheckout = async (listing_type, listing_id, package_id) => {
    try {
      const { data: d } = await api.post("/checkout/session", {
        listing_type, listing_id, package_id, origin_url: window.location.origin,
      });
      window.location.href = d.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitTool = async (e) => {
    e.preventDefault(); setBusy(true);
    const f = Object.fromEntries(new FormData(e.target));
    if (toolLogo) f.logo_url = toolLogo;
    try {
      if (!paymentsEnabled) {
        await api.post("/tools/checkout", { tier: "basic", origin_url: window.location.origin, form: {
          name: f.name, tagline: f.tagline, company: user?.company_name || user?.name || f.name,
          category: f.category, pricing_model: f.pricing_model, description: f.description,
          website: f.website, logo_url: toolLogo || undefined, contact_email: user?.email,
        } });
        setModal(null); toast.success("Tool submitted for review! We'll email you when it's live — you're on the WorldAI Weekly too.");
        return;
      }
      const { data: t } = await api.post("/tools", { ...f });
      setModal(null);
      if (f.tier === "free") { toast.success("Tool listed!"); load(); }
      else await startCheckout("tool", t.id, f.tier === "premium" ? "tool_premium" : "tool_featured");
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitJob = async (e) => {
    e.preventDefault(); setBusy(true);
    const f = Object.fromEntries(new FormData(e.target));
    f.remote = f.remote === "on";
    try {
      if (!paymentsEnabled) {
        await api.post("/jobs/checkout", { tier: "basic", origin_url: window.location.origin, form: {
          title: f.title, company: f.company_name, location: f.location, country: f.country,
          salary_from: f.salary || "", job_type: f.employment_type, is_remote: f.remote,
          description: f.description, apply_url: f.apply_url, contact_email: user?.email,
        } });
        setModal(null); toast.success("Job submitted for review! We'll email you when it's live — you're on the WorldAI Weekly too.");
        return;
      }
      const { data: j } = await api.post("/jobs", { ...f });
      setModal(null);
      const jobPkg = f.tier === "premium" ? "job_premium" : f.tier === "featured" ? "job_featured" : "job_standard";
      await startCheckout("job", j.id, jobPkg);
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitEvent = async (e) => {
    e.preventDefault(); setBusy(true);
    const f = Object.fromEntries(new FormData(e.target));
    if (eventBanner) f.banner_url = eventBanner;
    try {
      if (!paymentsEnabled) {
        await api.post("/events/checkout", { tier: "basic", origin_url: window.location.origin, form: {
          title: f.name, organizer: user?.company_name || user?.name || f.name,
          event_type: f.event_type, location: f.location, country: f.country,
          start_date: f.start_date, end_date: f.end_date, description: f.description,
          website: f.website, contact_email: user?.email,
        } });
        setModal(null); toast.success("Event submitted for review! We'll email you when it's live — you're on the WorldAI Weekly too.");
        return;
      }
      await api.post("/events", { ...f });
      setModal(null); toast.success("Event listed!"); load();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitProfile = async (e) => {
    e.preventDefault(); setBusy(true);
    const f = Object.fromEntries(new FormData(e.target));
    if (profileLogo) f.logo_url = profileLogo;
    try {
      await api.patch("/my/profile", { ...f });
      setModal(null); toast.success("Profile updated!");
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const tabs = [
    { id: "tools", label: "Tools", icon: Wrench },
    { id: "jobs", label: "Jobs", icon: Briefcase },
    { id: "events", label: "Events", icon: CalendarDays },
  ];

  return (
    <div data-testid="dashboard-page">
      <div className="border-b border-white/10 grid-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          <p className="font-mono-tech text-xs uppercase text-[#00F0FF] mb-2">Company Dashboard</p>
          <h1 className="font-display font-extrabold text-4xl">Welcome, {user?.name}</h1>
          <p className="text-[#94A3B8] mt-2">{user?.company_name || "Manage your listings"}</p>
          <div className="flex flex-wrap gap-3 mt-5">
            <button onClick={() => { setProfileLogo(""); setModal("profile"); }} data-testid="dash-edit-profile"
              className="flex items-center gap-2 border border-white/15 px-4 py-2 rounded-md text-sm hover:border-[#00F0FF]/60 transition">
              <Settings className="w-4 h-4" /> Edit company profile
            </button>
            <a href={`/company/${user?.id}`} target="_blank" rel="noreferrer" data-testid="dash-view-profile"
              className="flex items-center gap-2 text-sm text-[#00F0FF] px-4 py-2 hover:underline">
              <ExternalLink className="w-4 h-4" /> View public page
            </a>
            {user?.verified ? (
              <span className="flex items-center gap-2 text-sm text-[#00F0FF] border border-[#00F0FF]/40 bg-[#00F0FF]/10 px-4 py-2 rounded-md" data-testid="dash-verified">
                <BadgeCheck className="w-4 h-4" /> Verified company
              </span>
            ) : paymentsEnabled ? (
              <button onClick={() => startCheckout("company", user.id, "company_verified")} data-testid="dash-get-verified"
                className="flex items-center gap-2 text-sm border border-[#00F0FF]/40 text-[#00F0FF] px-4 py-2 rounded-md hover:bg-[#00F0FF]/10 transition">
                <BadgeCheck className="w-4 h-4" /> Get verified ($199)
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        {subInfo?.has_subscription && subInfo.message && (
          <div data-testid="subscription-banner"
            className={`mb-8 rounded-xl border p-4 flex items-center gap-3 ${subInfo.status === "past_due" ? "border-[#EF4444]/40 bg-[#EF4444]/10" : "border-[#22C55E]/40 bg-[#22C55E]/10"}`}>
            {subInfo.status === "past_due"
              ? <CreditCard className="w-5 h-5 text-[#f87171] shrink-0" />
              : <BadgeCheck className="w-5 h-5 text-[#4ade80] shrink-0" />}
            <p className={`text-sm ${subInfo.status === "past_due" ? "text-[#f87171]" : "text-[#4ade80]"}`}>
              {subInfo.message}
            </p>
          </div>
        )}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div className="flex gap-6 border-b border-white/10">
            {tabs.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`dash-tab-${t.id}`}
                className={`flex items-center gap-2 pb-3 -mb-px border-b-2 transition ${tab === t.id ? "border-[#00F0FF] text-[#00F0FF]" : "border-transparent text-[#94A3B8]"}`}>
                <t.icon className="w-4 h-4" /> {t.label} <span className="text-xs">({data[t.id].length})</span>
              </button>
            ))}
          </div>
          <button onClick={() => { setToolLogo(""); setEventBanner(""); setModal(tab); }} data-testid={`dash-add-${tab}`}
            className="flex items-center gap-2 bg-[#00F0FF] text-[#04121a] font-semibold px-4 py-2 rounded-md hover:brightness-110 transition">
            <Plus className="w-4 h-4" /> Add {tab.slice(0, -1)}
          </button>
        </div>

        <div className="space-y-3">
          {data[tab].length === 0 && <div className="text-[#94A3B8] py-16 text-center">No {tab} yet. Create your first one.</div>}
          {data[tab].map((item) => (
            <div key={item.id} className="card-surface rounded-lg p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`dash-item-${item.id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-display font-bold">{item.name || item.title}</span>
                  <Tier tier={item.tier} />
                </div>
                <p className="text-sm text-[#94A3B8]">{item.tagline || item.location || item.event_type}</p>
              </div>
              <div className="flex items-center gap-4">
                <StatusBadge status={item.status} />
                {item.status === "pending_payment" && paymentsEnabled && (
                  <button onClick={() => startCheckout(tab === "tools" ? "tool" : "job", item.id,
                    tab === "tools" ? (item.tier === "premium" ? "tool_premium" : "tool_featured") : (item.tier === "premium" ? "job_premium" : item.tier === "featured" ? "job_featured" : "job_standard"))}
                    data-testid={`dash-pay-${item.id}`}
                    className="flex items-center gap-1.5 text-sm bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 px-3 py-1.5 rounded-md hover:bg-[#00F0FF]/20">
                    <CreditCard className="w-4 h-4" /> Pay now
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {modal === "tools" && (
        <Modal title="List an AI Tool" onClose={() => setModal(null)}>
          <form onSubmit={submitTool} className="space-y-3">
            <input name="name" required placeholder="Tool name" className={field} data-testid="tool-name" />
            <input name="tagline" required placeholder="Short tagline" className={field} data-testid="tool-tagline" />
            <textarea name="description" required placeholder="Description" rows={3} className={field} data-testid="tool-description" />
            <div className="grid grid-cols-2 gap-3">
              <select name="category" className={field} data-testid="tool-category" defaultValue="Agents">
                {["Agents", "Vision", "Data", "Productivity", "Audio", "Code"].map((c) => <option key={c}>{c}</option>)}
              </select>
              <select name="pricing_model" className={field} defaultValue="Freemium">
                {["Free", "Freemium", "Paid"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <input name="website" required placeholder="https://website.com" className={field} data-testid="tool-website" />
            <ImageUpload label="Upload tool logo (optional)" onUploaded={setToolLogo} testid="tool-logo-upload" />
            {paymentsEnabled ? (
              <div>
                <label className="block text-sm text-[#94A3B8] mb-1">Listing tier</label>
                <select name="tier" className={field} data-testid="tool-tier" defaultValue="free">
                  <option value="free">Free — Basic listing ($0)</option>
                  <option value="featured">Featured — $59</option>
                  <option value="premium">Premium — $99 (top placement)</option>
                </select>
              </div>
            ) : (
              <p className="text-xs text-[#4ade80]">Free to list — reviewed and published within 24 hours.</p>
            )}
            <button disabled={busy} className="w-full bg-[#00F0FF] text-[#04121a] font-semibold py-2.5 rounded-md" data-testid="tool-submit">
              {busy ? "Processing…" : (paymentsEnabled ? "Continue" : "Submit for Review")}
            </button>
          </form>
        </Modal>
      )}

      {modal === "jobs" && (
        <Modal title="Post a Job" onClose={() => setModal(null)}>
          <form onSubmit={submitJob} className="space-y-3">
            <input name="title" required placeholder="Job title" className={field} data-testid="job-title" />
            <input name="company_name" required placeholder="Company name" defaultValue={user?.company_name || ""} className={field} data-testid="job-company" />
            <div className="grid grid-cols-2 gap-3">
              <input name="location" required placeholder="Location" className={field} data-testid="job-location" />
              <input name="country" required placeholder="Country" className={field} data-testid="job-country" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input name="salary" placeholder="Salary (optional)" className={field} />
              <select name="employment_type" className={field} defaultValue="Full-time">
                {["Full-time", "Part-time", "Contract", "Internship"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <textarea name="description" required placeholder="Job description" rows={3} className={field} data-testid="job-description" />
            <input name="apply_url" required placeholder="Application URL" className={field} data-testid="job-apply-url" />
            <label className="flex items-center gap-2 text-sm text-[#94A3B8]"><input type="checkbox" name="remote" data-testid="job-remote" /> Remote position</label>
            {paymentsEnabled ? (
              <div>
                <label className="block text-sm text-[#94A3B8] mb-1">Package (payment required)</label>
                <select name="tier" className={field} data-testid="job-tier" defaultValue="standard">
                  <option value="standard">Basic — $29</option>
                  <option value="featured">Featured — $49 (top placement)</option>
                  <option value="premium">Premium — $79 (homepage placement)</option>
                </select>
              </div>
            ) : (
              <p className="text-xs text-[#4ade80]">Free to post — reviewed and published within 24 hours.</p>
            )}
            <button disabled={busy} className="w-full bg-[#00F0FF] text-[#04121a] font-semibold py-2.5 rounded-md" data-testid="job-submit">
              {busy ? "Processing…" : (paymentsEnabled ? "Continue to payment" : "Submit for Review")}
            </button>
          </form>
        </Modal>
      )}

      {modal === "events" && (
        <Modal title="List an Event" onClose={() => setModal(null)}>
          <form onSubmit={submitEvent} className="space-y-3">
            <input name="name" required placeholder="Event name" className={field} data-testid="event-name" />
            <select name="event_type" className={field} defaultValue="Conference" data-testid="event-type-select">
              {["Conference", "Hackathon", "Summit"].map((c) => <option key={c}>{c}</option>)}
            </select>
            <textarea name="description" required placeholder="Description" rows={3} className={field} data-testid="event-description" />
            <div className="grid grid-cols-2 gap-3">
              <input name="location" required placeholder="City" className={field} data-testid="event-location" />
              <input name="country" required placeholder="Country" className={field} data-testid="event-country" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-[#94A3B8]">Start date</label><input name="start_date" type="date" required className={field} data-testid="event-start" /></div>
              <div><label className="text-xs text-[#94A3B8]">End date</label><input name="end_date" type="date" className={field} /></div>
            </div>
            <input name="website" required placeholder="Event website" className={field} data-testid="event-website" />
            <ImageUpload label="Upload event banner (optional)" onUploaded={setEventBanner} testid="event-banner-upload" />
            <button disabled={busy} className="w-full bg-[#00F0FF] text-[#04121a] font-semibold py-2.5 rounded-md" data-testid="event-submit">
              {busy ? "Saving…" : (paymentsEnabled ? "Publish event (free)" : "Submit for Review")}
            </button>
          </form>
        </Modal>
      )}

      {modal === "profile" && (
        <Modal title="Company Profile" onClose={() => setModal(null)}>
          <form onSubmit={submitProfile} className="space-y-3">
            <input name="company_name" placeholder="Company name" defaultValue={user?.company_name || ""} className={field} data-testid="profile-company-name" />
            <input name="country" placeholder="Country" defaultValue={user?.country || ""} className={field} data-testid="profile-country" />
            <input name="website" placeholder="Website (https://…)" defaultValue={user?.website || ""} className={field} data-testid="profile-website" />
            <textarea name="bio" placeholder="Short company bio" rows={3} defaultValue={user?.bio || ""} className={field} data-testid="profile-bio" />
            <ImageUpload label="Upload company logo (optional)" onUploaded={setProfileLogo} testid="profile-logo-upload" />
            <button disabled={busy} className="w-full bg-[#00F0FF] text-[#04121a] font-semibold py-2.5 rounded-md" data-testid="profile-submit">
              {busy ? "Saving…" : "Save profile"}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}
